/**
 * Scheduled email (`docs/UTILITIES.md` §10): whether THIS server sends each
 * email, and the log of what it sent.
 *
 * The lists above say who an email goes to and when; this says how it goes on
 * this server -- Off (a draft, downloaded by hand as before), Test (only to a
 * test address, marked [TEST]) or Live -- plus who a reply reaches, and for the
 * rep email which business day and any extra copies. The settings belong to
 * the server, not to the shared lists, so switching production on never makes
 * the workstation send.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Send } from 'lucide-react'
import { useState } from 'react'

import { Badge, Button, Card, ConfirmDialog, Grid, TextField } from '../../components/admin/ui'
import { ErrorState, Skeleton } from '../../components/States'
import { ApiError } from '../../lib/api'
import { formatDateTime } from '../../lib/format'
import type { MailJob, MailMode, MailOverview, MailSendRow, MailSettings } from './api'
import { mailApi } from './api'

const MAIL_KEY = ['utility', 'sales-lists', 'mail'] as const

const SELECT =
  'mt-1 w-full rounded-[var(--radius-md)] border border-[var(--color-rule)] bg-[var(--color-surface)] px-2 py-1.5 text-[length:var(--text-sm)]'

const MODES: readonly { value: MailMode; label: string }[] = [
  { value: 'off', label: 'Off: draft only, sent by hand' },
  { value: 'test', label: 'Test: only to the test address' },
  { value: 'live', label: 'Live: to its recipients' },
]

const STATUS_TONE = {
  sent: 'positive',
  skipped: 'warning',
  failed: 'danger',
  sending: 'neutral',
} as const

const orNull = (value: string) => (value.trim() === '' ? null : value.trim())

function settingsOf(job: MailJob): MailSettings {
  return {
    mode: job.mode,
    test_to: job.test_to,
    reply_to: job.reply_to,
    business_day: job.business_day,
    extra_cc: job.extra_cc === '' ? null : job.extra_cc,
  }
}

function hour(value: number): string {
  const suffix = value < 12 ? 'a.m.' : 'p.m.'
  const twelve = value % 12 === 0 ? 12 : value % 12
  return `${String(twelve)}:00 ${suffix}`
}

export function Sending() {
  const overview = useQuery({ queryKey: MAIL_KEY, queryFn: mailApi.overview })
  if (overview.isPending) return <Skeleton rows={3} />
  if (overview.data === undefined) {
    return <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />
  }
  const data = overview.data
  return (
    <>
      <Card
        title="Sending"
        description={
          data.configured
            ? `On this server, scheduled emails go out from ${data.sender ?? ''} at ${hour(data.send_hour)} (${data.timezone}) on their day. Each email is Off, Test or Live here; a new one starts Off.`
            : 'Each email is Off, Test or Live on this server; a new one starts Off.'
        }
      >
        {!data.configured && (
          <p
            role="status"
            className="mb-4 rounded-[var(--radius-md)] border border-[#b8860b]/50 p-2 text-[length:var(--text-xs)]"
          >
            <strong>This server is not set up to send yet.</strong> Until the DoNotReply
            permission from IT is in place (MAIL_* settings), an email set to Test or Live is
            logged below as not sent. Its draft can still be downloaded from its report.
          </p>
        )}
        {data.lists_error !== null && (
          <p role="alert" className="mb-4 text-[length:var(--text-xs)] text-[var(--color-negative)]">
            {data.lists_error}
          </p>
        )}
        <div className="grid gap-4 lg:grid-cols-3">
          {data.jobs.map((job) => (
            <JobEditor key={job.key} job={job} />
          ))}
        </div>
      </Card>
      <SendLog data={data} />
    </>
  )
}

function JobEditor({ job }: { job: MailJob }) {
  const queryClient = useQueryClient()
  const saved = settingsOf(job)
  const [draft, setDraft] = useState<MailSettings>(saved)
  const [confirmSend, setConfirmSend] = useState(false)
  const dirty = JSON.stringify(draft) !== JSON.stringify(saved)
  const isReps = job.list_name === null

  const save = useMutation({
    mutationFn: () => mailApi.save(job.key, draft),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: MAIL_KEY }),
  })
  const send = useMutation({
    mutationFn: () => mailApi.sendNow(job.key),
    onSettled: () => {
      setConfirmSend(false)
      void queryClient.invalidateQueries({ queryKey: MAIL_KEY })
    },
  })
  const fields = save.error instanceof ApiError ? save.error.fields : {}
  const recipients = isReps
    ? 'Each rep, their manager copied (Sales teams above).'
    : job.to === null
      ? 'Its list could not be read.'
      : job.to.length === 0
        ? `Nobody yet: add people to the ${job.list_name} list above.`
        : `To ${job.to.join(', ')}${job.cc !== null && job.cc.length > 0 ? `; CC ${job.cc.join(', ')}` : ''}`

  return (
    <section
      className="rounded-[var(--radius-md)] border border-[var(--color-rule)] p-3"
      aria-label={`${job.title} sending`}
    >
      <div className="flex items-center gap-2">
        <h3 className="text-[length:var(--text-sm)] font-semibold">{job.title}</h3>
        <Badge tone={job.mode === 'live' ? 'positive' : job.mode === 'test' ? 'warning' : 'neutral'}>
          {job.mode === 'live' ? 'Live' : job.mode === 'test' ? 'Test' : 'Off'}
        </Badge>
      </div>
      <p className="mt-1 text-[length:var(--text-xs)] text-[var(--color-ink-muted)]">{job.about}</p>
      <dl className="mt-2 space-y-1 text-[length:var(--text-xs)]">
        <div>
          <dt className="inline font-medium">When: </dt>
          <dd className="inline">
            {job.schedule ?? 'Unknown (its list could not be read)'}
            {job.next_send !== null && job.mode !== 'off'
              ? ` · next ${formatDateTime(job.next_send)}`
              : ''}
          </dd>
        </div>
        <div>
          <dt className="inline font-medium">Goes to: </dt>
          <dd className="inline">{recipients}</dd>
        </div>
      </dl>

      <div className="mt-3 space-y-2">
        <label className="block">
          <span className="block text-[length:var(--text-xs)] font-medium">On this server</span>
          <select
            className={SELECT}
            value={draft.mode}
            onChange={(event) => {
              setDraft({ ...draft, mode: event.target.value as MailMode })
            }}
          >
            {MODES.map((mode) => (
              <option key={mode.value} value={mode.value}>
                {mode.label}
              </option>
            ))}
          </select>
        </label>
        <TextField
          label="Test copies go to"
          type="email"
          value={draft.test_to ?? ''}
          error={fields['test_to']}
          onChange={(event) => {
            setDraft({ ...draft, test_to: orNull(event.target.value) })
          }}
        />
        <TextField
          label="Replies go to"
          type="email"
          hint="Left empty, a reply goes to DoNotReply and nobody reads it."
          value={draft.reply_to ?? ''}
          error={fields['reply_to']}
          onChange={(event) => {
            setDraft({ ...draft, reply_to: orNull(event.target.value) })
          }}
        />
        {isReps && (
          <>
            <label className="block">
              <span className="block text-[length:var(--text-xs)] font-medium">
                Business day after month end
              </span>
              <select
                className={SELECT}
                value={String(draft.business_day ?? 3)}
                onChange={(event) => {
                  setDraft({ ...draft, business_day: Number(event.target.value) })
                }}
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((day) => (
                  <option key={day} value={day}>
                    {day}
                  </option>
                ))}
              </select>
            </label>
            <TextField
              label="Also copy (comma-separated)"
              value={draft.extra_cc ?? ''}
              error={fields['extra_cc']}
              onChange={(event) => {
                setDraft({ ...draft, extra_cc: orNull(event.target.value) })
              }}
            />
          </>
        )}
      </div>

      {save.error !== null && (
        <p role="alert" className="mt-2 text-[length:var(--text-xs)] text-[var(--color-negative)]">
          {save.error.message}
        </p>
      )}
      {send.error !== null && (
        <p role="alert" className="mt-2 text-[length:var(--text-xs)] text-[var(--color-negative)]">
          {send.error.message}
        </p>
      )}
      {send.data !== undefined && (
        <p role="status" className="mt-2 text-[length:var(--text-xs)]">
          Sent {send.data.sent}, not sent {send.data.failed}, skipped {send.data.skipped}.
          {send.data.problems.length > 0 && ` ${send.data.problems[0] ?? ''}`}
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button
          variant="primary"
          disabled={!dirty || save.isPending}
          onClick={() => {
            save.mutate()
          }}
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button
          disabled={dirty || job.mode === 'off' || send.isPending}
          title={
            job.mode === 'off'
              ? 'Set it to Test or Live first'
              : dirty
                ? 'Save your changes first'
                : undefined
          }
          onClick={() => {
            setConfirmSend(true)
          }}
        >
          <Send aria-hidden className="size-3.5" /> {send.isPending ? 'Sending…' : 'Send now'}
        </Button>
        {dirty && <Badge tone="warning">Not saved</Badge>}
      </div>

      <ConfirmDialog
        open={confirmSend}
        title={`Send ${job.title} now?`}
        body={
          job.mode === 'test'
            ? `A test copy of every message goes to ${job.test_to ?? ''} only.`
            : isReps
              ? 'Every rep gets their email for the month just closed, their manager copied.'
              : `It goes to its list now: ${recipients}`
        }
        confirmLabel="Send"
        onConfirm={() => {
          send.mutate()
        }}
        onCancel={() => {
          setConfirmSend(false)
        }}
      />
    </section>
  )
}

function SendLog({ data }: { data: MailOverview }) {
  const titles = new Map(data.jobs.map((job) => [job.key, job.title]))
  return (
    <Card
      title="Sent"
      description="Every email this server has sent or tried to, newest first. In Test, To and CC are who it would have gone to."
    >
      {data.sends.length === 0 ? (
        <p className="text-[length:var(--text-xs)] text-[var(--color-ink-muted)]">Nothing yet.</p>
      ) : (
        <Grid head={['When', 'Email', 'For', 'How', 'Result', 'To', 'CC', 'Detail']}>
          {data.sends.map((row: MailSendRow) => (
            <tr key={row.id} className="border-b border-[var(--color-rule)] align-top">
              <td className="px-2 py-1 whitespace-nowrap">{formatDateTime(row.created_at)}</td>
              <td className="px-2 py-1">{titles.get(row.job) ?? row.job}</td>
              <td className="px-2 py-1">
                {row.recipient === '*' ? 'The list' : row.recipient === '!build' ? '' : row.recipient}
              </td>
              <td className="px-2 py-1 whitespace-nowrap">
                {row.mode === 'test' ? 'Test' : 'Live'}, {row.trigger === 'manual' ? 'by hand' : 'scheduled'}
              </td>
              <td className="px-2 py-1">
                <Badge tone={STATUS_TONE[row.status]}>
                  {row.status === 'failed' ? 'not sent' : row.status}
                </Badge>
              </td>
              <td className="px-2 py-1">{row.to}</td>
              <td className="px-2 py-1">{row.cc}</td>
              <td className="px-2 py-1 text-[var(--color-ink-muted)]">{row.detail}</td>
            </tr>
          ))}
        </Grid>
      )}
    </Card>
  )
}
